(() => {
  'use strict';

  if (window.__blistopAccessibilityWidgetLoaded) return;
  window.__blistopAccessibilityWidgetLoaded = true;

  const STORAGE_KEY = 'blistopAccessibilityPreferences';
  const TEXT_SCALES = [0.9, 1, 1.15, 1.3, 1.5, 1.75, 2];
  const CONTRAST_MODES = ['none', 'dark', 'light', 'monochrome'];
  const CURSOR_MODES = ['default', 'black', 'white'];
  const DEFAULT_STATE = Object.freeze({
    contrastMode: 'none',
    lowSaturation: false,
    visualFocus: false,
    readableFont: false,
    stopAnimations: false,
    highlightLinks: false,
    highlightHeadings: false,
    largeControls: false,
    cursorMode: 'default',
    textScale: 1,
  });
  const FEATURE_CLASSES = Object.freeze({
    lowSaturation: 'accessibility-low-saturation',
    visualFocus: 'accessibility-visual-focus',
    readableFont: 'accessibility-readable-font',
    stopAnimations: 'accessibility-stop-animations',
    highlightLinks: 'accessibility-highlight-links',
    highlightHeadings: 'accessibility-highlight-headings',
    largeControls: 'accessibility-large-controls',
  });
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  let state = loadState();
  let activeWidget = null;
  let speechActive = false;
  let speechUtterance = null;

  function normalizeState(value) {
    const legacyContrast = value?.highContrast === true ? 'dark' : 'none';
    const contrastMode = CONTRAST_MODES.includes(value?.contrastMode) ? value.contrastMode : legacyContrast;
    const cursorMode = CURSOR_MODES.includes(value?.cursorMode) ? value.cursorMode : 'default';
    const textScale = TEXT_SCALES.includes(Number(value?.textScale)) ? Number(value.textScale) : 1;

    return {
      contrastMode,
      lowSaturation: value?.lowSaturation === true,
      visualFocus: value?.visualFocus === true,
      readableFont: value?.readableFont === true,
      stopAnimations: value?.stopAnimations === true,
      highlightLinks: value?.highlightLinks === true,
      highlightHeadings: value?.highlightHeadings === true,
      largeControls: value?.largeControls === true,
      cursorMode,
      textScale,
    };
  }

  function loadState() {
    try {
      return normalizeState(JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'));
    } catch (_error) {
      return { ...DEFAULT_STATE };
    }
  }

  function saveState() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_error) {
      // Preferences continue to work for the current page when storage is unavailable.
    }
  }

  function activePreferenceCount() {
    const booleanCount = Object.keys(FEATURE_CLASSES).filter((feature) => state[feature]).length;
    return booleanCount + Number(state.contrastMode !== 'none') + Number(state.cursorMode !== 'default') + Number(state.textScale !== 1);
  }

  function applyState() {
    const body = document.body;
    const root = document.documentElement;
    if (!body) return;

    Object.entries(FEATURE_CLASSES).forEach(([feature, className]) => {
      const target = feature === 'lowSaturation' ? root : body;
      target.classList.toggle(className, state[feature]);
    });

    body.classList.toggle('accessibility-high-contrast', state.contrastMode === 'dark');
    body.classList.toggle('accessibility-light-contrast', state.contrastMode === 'light');
    root.classList.toggle('accessibility-monochrome', state.contrastMode === 'monochrome');
    body.classList.toggle('accessibility-cursor-black', state.cursorMode === 'black');
    body.classList.toggle('accessibility-cursor-white', state.cursorMode === 'white');
    body.classList.toggle('accessibility-large-text', state.textScale > 1);
    body.classList.toggle('accessibility-small-text', state.textScale < 1);
    root.style.setProperty('--accessibility-text-base-size', `${62.5 * state.textScale}%`);

    document.querySelectorAll('[data-accessibility-widget]').forEach(syncControls);
  }

  function setPressedState(button, isActive) {
    button.setAttribute('aria-pressed', String(isActive));
    const stateLabel = button.querySelector('[data-accessibility-state]');
    if (stateLabel) stateLabel.textContent = isActive ? 'פעיל' : 'כבוי';
  }

  function syncControls(widget) {
    widget.querySelectorAll('[data-accessibility-feature]').forEach((button) => {
      setPressedState(button, state[button.dataset.accessibilityFeature] === true);
    });
    widget.querySelectorAll('[data-accessibility-contrast]').forEach((button) => {
      setPressedState(button, state.contrastMode === button.dataset.accessibilityContrast);
    });
    widget.querySelectorAll('[data-accessibility-cursor]').forEach((button) => {
      setPressedState(button, state.cursorMode === button.dataset.accessibilityCursor);
    });

    const scaleLabel = widget.querySelector('[data-accessibility-scale]');
    if (scaleLabel) scaleLabel.textContent = `${Math.round(state.textScale * 100)}%`;

    const decreaseButton = widget.querySelector('[data-accessibility-action="decreaseText"]');
    const increaseButton = widget.querySelector('[data-accessibility-action="increaseText"]');
    const resetTextButton = widget.querySelector('[data-accessibility-action="resetText"]');
    const resetAllButton = widget.querySelector('[data-accessibility-action="resetAll"]');
    if (decreaseButton) decreaseButton.disabled = state.textScale === TEXT_SCALES[0];
    if (increaseButton) increaseButton.disabled = state.textScale === TEXT_SCALES[TEXT_SCALES.length - 1];
    if (resetTextButton) resetTextButton.disabled = state.textScale === 1;
    if (resetAllButton) resetAllButton.disabled = activePreferenceCount() === 0 && !speechActive;

    const count = activePreferenceCount();
    const countLabel = widget.querySelector('[data-accessibility-active-count]');
    if (countLabel) {
      countLabel.textContent = count === 0 ? 'אין התאמות פעילות' : count === 1 ? 'התאמה אחת פעילה' : `${count} התאמות פעילות`;
    }

    const speechButton = widget.querySelector('[data-accessibility-action="readAloud"]');
    if (speechButton) {
      const supported = 'speechSynthesis' in window && 'SpeechSynthesisUtterance' in window;
      speechButton.disabled = !supported;
      speechButton.setAttribute('aria-pressed', String(speechActive));
      const speechLabel = speechButton.querySelector('[data-accessibility-speech-state]');
      if (speechLabel) speechLabel.textContent = supported ? (speechActive ? 'עצירת הקראה' : 'הפעלת הקראה') : 'לא נתמך בדפדפן זה';
    }
  }

  function updateState(mutator) {
    mutator(state);
    state = normalizeState(state);
    saveState();
    applyState();
  }

  function announce(widget, message) {
    const announcer = widget.querySelector('[data-accessibility-announcer]');
    if (!announcer) return;
    announcer.textContent = '';
    requestAnimationFrame(() => {
      announcer.textContent = message;
    });
  }

  function getFocusableElements(panel) {
    return Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
      return !element.hidden && element.getClientRects().length > 0;
    });
  }

  function openWidget(widget) {
    if (activeWidget && activeWidget !== widget) closeWidget(activeWidget, false);

    const layer = widget.querySelector('[data-accessibility-layer]');
    const trigger = widget.querySelector('[data-accessibility-trigger]');
    const panel = widget.querySelector('[data-accessibility-panel]');
    if (!layer || !trigger || !panel) return;

    activeWidget = widget;
    layer.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('accessibility-panel-open');
    requestAnimationFrame(() => panel.focus({ preventScroll: true }));
  }

  function closeWidget(widget, restoreFocus = true) {
    const layer = widget.querySelector('[data-accessibility-layer]');
    const trigger = widget.querySelector('[data-accessibility-trigger]');
    if (!layer || !trigger) return;

    layer.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('accessibility-panel-open');
    if (activeWidget === widget) activeWidget = null;
    if (restoreFocus) trigger.focus({ preventScroll: true });
  }

  function toggleSpeech(widget) {
    if (!('speechSynthesis' in window) || !('SpeechSynthesisUtterance' in window)) {
      announce(widget, 'הקראת טקסט אינה נתמכת בדפדפן זה');
      return;
    }

    if (speechActive) {
      window.speechSynthesis.cancel();
      speechActive = false;
      speechUtterance = null;
      document.querySelectorAll('[data-accessibility-widget]').forEach(syncControls);
      announce(widget, 'הקראת התוכן נעצרה');
      return;
    }

    const selection = window.getSelection()?.toString().trim();
    const mainContent = document.querySelector('#MainContent')?.innerText.trim();
    const text = selection || mainContent;
    if (!text) {
      announce(widget, 'לא נמצא תוכן להקראה בעמוד זה');
      return;
    }

    window.speechSynthesis.cancel();
    speechUtterance = new SpeechSynthesisUtterance(text.slice(0, 20000));
    speechUtterance.lang = document.documentElement.lang || 'he-IL';
    speechUtterance.rate = 0.9;
    speechUtterance.onend = finishSpeech;
    speechUtterance.onerror = finishSpeech;
    speechActive = true;
    document.querySelectorAll('[data-accessibility-widget]').forEach(syncControls);
    window.speechSynthesis.speak(speechUtterance);
    announce(widget, selection ? 'הקראת הטקסט המסומן החלה' : 'הקראת תוכן העמוד החלה');
  }

  function finishSpeech() {
    speechActive = false;
    speechUtterance = null;
    document.querySelectorAll('[data-accessibility-widget]').forEach(syncControls);
  }

  function resetAll(widget) {
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    speechActive = false;
    speechUtterance = null;
    state = { ...DEFAULT_STATE };
    saveState();
    applyState();
    announce(widget, 'כל התאמות הנגישות אופסו');
  }

  function optionLabel(button) {
    return button.querySelector('strong')?.textContent.trim() || button.textContent.trim();
  }

  function handleWidgetClick(event) {
    const widget = event.currentTarget;
    const trigger = event.target.closest('[data-accessibility-trigger]');
    if (trigger) {
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      if (isOpen) closeWidget(widget);
      else openWidget(widget);
      return;
    }

    if (event.target.closest('[data-accessibility-close]') || event.target.closest('[data-accessibility-backdrop]')) {
      closeWidget(widget);
      return;
    }

    const featureButton = event.target.closest('[data-accessibility-feature]');
    if (featureButton) {
      const feature = featureButton.dataset.accessibilityFeature;
      if (Object.prototype.hasOwnProperty.call(FEATURE_CLASSES, feature)) {
        const willActivate = !state[feature];
        updateState((nextState) => {
          nextState[feature] = willActivate;
        });
        announce(widget, `${optionLabel(featureButton)} ${willActivate ? 'הופעל' : 'כובה'}`);
      }
      return;
    }

    const contrastButton = event.target.closest('[data-accessibility-contrast]');
    if (contrastButton) {
      const mode = contrastButton.dataset.accessibilityContrast;
      const willActivate = state.contrastMode !== mode;
      updateState((nextState) => {
        nextState.contrastMode = willActivate ? mode : 'none';
      });
      announce(widget, `${optionLabel(contrastButton)} ${willActivate ? 'הופעל' : 'כובה'}`);
      return;
    }

    const cursorButton = event.target.closest('[data-accessibility-cursor]');
    if (cursorButton) {
      const mode = cursorButton.dataset.accessibilityCursor;
      const willActivate = state.cursorMode !== mode;
      updateState((nextState) => {
        nextState.cursorMode = willActivate ? mode : 'default';
      });
      announce(widget, `${optionLabel(cursorButton)} ${willActivate ? 'הופעל' : 'כובה'}`);
      return;
    }

    const actionButton = event.target.closest('[data-accessibility-action]');
    if (!actionButton) return;

    const action = actionButton.dataset.accessibilityAction;
    const currentIndex = TEXT_SCALES.indexOf(state.textScale);
    if (action === 'increaseText') {
      updateState((nextState) => {
        nextState.textScale = TEXT_SCALES[Math.min(currentIndex + 1, TEXT_SCALES.length - 1)];
      });
      announce(widget, `גודל הטקסט הוגדל ל-${Math.round(state.textScale * 100)} אחוזים`);
    } else if (action === 'decreaseText') {
      updateState((nextState) => {
        nextState.textScale = TEXT_SCALES[Math.max(currentIndex - 1, 0)];
      });
      announce(widget, `גודל הטקסט הוקטן ל-${Math.round(state.textScale * 100)} אחוזים`);
    } else if (action === 'resetText') {
      updateState((nextState) => {
        nextState.textScale = 1;
      });
      announce(widget, 'גודל הטקסט אופס');
    } else if (action === 'readAloud') {
      toggleSpeech(widget);
    } else if (action === 'resetAll') {
      resetAll(widget);
    }
  }

  function handleDocumentKeydown(event) {
    if (event.ctrlKey && event.key === 'F11') {
      event.preventDefault();
      const widget = activeWidget || document.querySelector('[data-accessibility-widget]');
      if (!widget) return;
      if (activeWidget) closeWidget(widget);
      else openWidget(widget);
      return;
    }

    if (!activeWidget) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closeWidget(activeWidget);
      return;
    }

    if (event.key !== 'Tab') return;

    const panel = activeWidget.querySelector('[data-accessibility-panel]');
    if (!panel) return;
    const focusableElements = getFocusableElements(panel);
    if (focusableElements.length === 0) {
      event.preventDefault();
      panel.focus();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    if (document.activeElement === panel) {
      event.preventDefault();
      (event.shiftKey ? lastElement : firstElement).focus();
    } else if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  function handleDocumentFocus(event) {
    if (!activeWidget) return;
    const panel = activeWidget.querySelector('[data-accessibility-panel]');
    if (panel && !panel.contains(event.target)) {
      panel.focus({ preventScroll: true });
    }
  }

  function initializeWidget(widget) {
    if (widget.dataset.accessibilityInitialized === 'true') return;

    widget.dataset.accessibilityInitialized = 'true';
    widget.addEventListener('click', handleWidgetClick);
    document.body.classList.remove('accessibility-widget-position-left', 'accessibility-widget-position-right');
    document.body.classList.add(`accessibility-widget-position-${widget.dataset.position || 'left'}`);
    syncControls(widget);
  }

  function initialize(root = document) {
    root.querySelectorAll('[data-accessibility-widget]').forEach(initializeWidget);
    applyState();
  }

  function boot() {
    initialize();
    document.addEventListener('keydown', handleDocumentKeydown, true);
    document.addEventListener('focusin', handleDocumentFocus, true);
    document.addEventListener('shopify:section:load', (event) => initialize(event.target));
    window.addEventListener('storage', (event) => {
      if (event.key !== STORAGE_KEY) return;
      state = loadState();
      applyState();
    });
    window.addEventListener('pagehide', () => {
      if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
